import React, { useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Stack, Typography } from '@mui/material';

import { Order } from '@luxuryescapes/contract-svc-order';
import { formatDate } from '@luxuryescapes/lib-refunds';

import { sortBy } from '~/utils/arrayUtils';

import { CancellationPoliciesText } from './CancellationPoliciesText';
import { RefundPolicy } from './RefundPolicies';

interface Props {
  room: Order.BedbankItemRoom;
  refundPolicy: RefundPolicy;
  propertyTimezone: string;
  refundIssuer: 'Vendor' | 'LE';
  nights: number;
}

function SimpleRefundPolicies({ room, refundPolicy, propertyTimezone, refundIssuer, nights }: Props) {
  const [showPartialRefundDetails, setShowPartialRefundDetails] = useState(false);

  const { refundable, partiallyRefundable, cancellationPolicy } = refundPolicy;

  if (refundable) {
    const sortedPoliciesDesc = sortBy(cancellationPolicy.policies, (cp) => cp.start, 'desc');
    const now = new Date();
    const applicablePolicy = sortedPoliciesDesc.find((cp) => cp.start <= now);

    // if all penalty start are in the future, then it is still fully refundable
    const fullyRefundable = sortedPoliciesDesc.length && !applicablePolicy;

    const sortedPolicies = sortBy(cancellationPolicy.policies, (cp) => cp.start, 'asc');
    const applicablePolicyIndex = sortedPolicies.indexOf(applicablePolicy);

    return (
      <Stack spacing={1}>
        <Box>
          <Typography
            sx={{ display: 'inline', color: fullyRefundable ? 'success.main' : 'error.main' }}
            component="span"
          >
            {fullyRefundable ? '' : '(Expired) '}
            Free cancellation
          </Typography>
          {!!cancellationPolicy.policies.length && (
            <Typography sx={{ display: 'inline', color: 'text.secondary' }} component="span">
              {' '}
              until {formatDate.formattedDateByTimezone(cancellationPolicy.policies[0]?.start, propertyTimezone)} (local
              time of property)
            </Typography>
          )}
        </Box>

        {sortedPolicies.length > 0 && (
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {sortedPolicies.map((policy, i) => {
              return (
                <li key={i}>
                  <CancellationPoliciesText
                    policy={policy}
                    nextPolicy={sortedPolicies[i + 1]}
                    refundIssuer={refundIssuer}
                    room={room}
                    isExpired={applicablePolicyIndex > i}
                    isActive={applicablePolicyIndex === i}
                    propertyTimezone={propertyTimezone}
                  />
                </li>
              );
            })}
          </Box>
        )}
      </Stack>
    );
  }
  if (partiallyRefundable) {
    return (
      <Stack spacing={1}>
        <Typography sx={{ color: 'success.main' }} component="div">
          Partially Refundable
        </Typography>
        <Stack spacing={1}>
          <Button
            sx={{ color: 'text.secondary', p: 0, minWidth: 'auto', textAlign: 'left' }}
            onClick={() => setShowPartialRefundDetails((state) => !state)}
          >
            {showPartialRefundDetails ? 'Hide Details' : 'Show Details'}
            {showPartialRefundDetails ? (
              <ExpandLessIcon sx={{ color: 'text.secondary' }} />
            ) : (
              <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
            )}
          </Button>
          {showPartialRefundDetails && !!cancellationPolicy.description.length && (
            <Box color="text.primary">
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {cancellationPolicy.description.map((description, i) => (
                  <li key={i}>{description}</li>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </Stack>
    );
  }

  if (!partiallyRefundable && !refundable) {
    return (
      <Typography sx={{ color: 'error.main' }} component="div">
        Non-refundable
      </Typography>
    );
  }

  return null;
}

export default SimpleRefundPolicies;
